/*
 *  Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
 *
 *  Licensed under the Apache License, Version 2.0 (the "License").
 *  You may not use this file except in compliance with the License.
 *  A copy of the License is located at
 *
 *  http://aws.amazon.com/apache2.0
 *
 *  or in the "license" file accompanying this file. This file is distributed
 *  on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 *  express or implied. See the License for the specific language governing
 *  permissions and limitations under the License.
 */

import _ from 'lodash';
import React from 'react';
import { inject, observer } from 'mobx-react';
import { withRouter } from 'react-router-dom';
import { decorate, observable, action, runInAction } from 'mobx';
import { Icon } from 'semantic-ui-react';
import { displayError } from '@amzn/base-ui/dist/helpers/notification';

const iconStyle = { margin: 0, lineHeight: 1, height: '1em' };

/**
 * Reads a configured value, using the fallback only when the variable is absent altogether.
 *
 * An explicitly empty setting is honoured rather than replaced, so a stage can blank out a step or
 * the help line. The fallbacks exist for builds made against an older .env that predates these
 * variables, not to paper over deliberate blanks.
 */
function setting(value, fallback) {
  return _.isNil(value) ? fallback : value;
}

// Page copy is driven by per-stage settings, see the accessDenied* entries in config/settings.
const copy = {
  title: setting(process.env.REACT_APP_ACCESS_DENIED_TITLE, 'Sign-Up Required'),
  subtitle: setting(
    process.env.REACT_APP_ACCESS_DENIED_SUBTITLE,
    'Your account does not currently have access to this platform.',
  ),
  instructionsTitle: setting(process.env.REACT_APP_ACCESS_DENIED_INSTRUCTIONS_TITLE, 'How to get access'),
  steps: [
    setting(process.env.REACT_APP_ACCESS_DENIED_STEP_1, 'Install and open the *RACE app* within Microsoft Teams.'),
    setting(process.env.REACT_APP_ACCESS_DENIED_STEP_2, 'Sign up for an account through the app.'),
    setting(process.env.REACT_APP_ACCESS_DENIED_STEP_3, 'Request access to the *RACE Compute Service*.'),
  ].filter(step => !_.isEmpty(_.trim(step))),
  buttonLabel: setting(process.env.REACT_APP_ACCESS_DENIED_BUTTON_LABEL, 'View Sign-Up Instructions'),
  buttonUrl: setting(process.env.REACT_APP_ACCESS_DENIED_BUTTON_URL, process.env.REACT_APP_REQUEST_ACCESS_URL),
  helpText: setting(process.env.REACT_APP_ACCESS_DENIED_HELP_TEXT, 'Need help? Contact us at'),
  helpEmail: setting(process.env.REACT_APP_ACCESS_DENIED_HELP_EMAIL, 'race@rmit.edu.au'),
};

/**
 * Renders configured copy, converting *asterisk wrapped* runs to bold text.
 *
 * The result is an array of React nodes, never raw HTML, so configured strings cannot inject
 * markup or script. Unmatched asterisks are left as literal characters.
 */
function renderCopy(text) {
  if (_.isEmpty(text)) return null;

  const pattern = /\*([^*]+)\*/g;
  const nodes = [];
  let cursor = 0;
  let match = pattern.exec(text);

  while (match !== null) {
    if (match.index > cursor) nodes.push(text.slice(cursor, match.index));
    nodes.push(<strong key={`strong-${match.index}`}>{match[1]}</strong>);
    cursor = match.index + match[0].length;
    match = pattern.exec(text);
  }

  if (cursor < text.length) nodes.push(text.slice(cursor));

  return nodes;
}

class UserApplication extends React.Component {
  constructor(props) {
    super(props);
    runInAction(() => {
      this.logoutProcessing = false;
      this.currentUser = props.userStore.cloneUser;
    });
  }

  handleLogout = action(async event => {
    this.logoutProcessing = true;
    event.preventDefault();
    event.stopPropagation();
    try {
      await this.props.authentication.logout();
      runInAction(() => { this.logoutProcessing = false; });
    } catch (error) {
      displayError(error);
      runInAction(() => { this.logoutProcessing = false; });
    }
  });

  render() {
    const processing = this.logoutProcessing;

    return (
      <div className="access-denied-wrapper">
        <div className="workspace-card access-denied-card">
          <div className="workspace-card-accent" style={{ background: 'var(--rmit-red)' }} />
          <div className="access-denied-body">
            <div className="access-denied-icon">
              <Icon name="lock" style={iconStyle} />
            </div>

            <h2 className="access-denied-title">{copy.title}</h2>
            {!_.isEmpty(copy.subtitle) && <p className="access-denied-subtitle">{renderCopy(copy.subtitle)}</p>}

            {!_.isEmpty(copy.steps) && (
              <div className="access-denied-instructions">
                {!_.isEmpty(copy.instructionsTitle) && (
                  <div className="access-denied-instructions-title">{copy.instructionsTitle}</div>
                )}
                <div className="access-denied-steps">
                  {_.map(copy.steps, (step, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <div className="access-denied-step" key={`step-${index}`}>
                      <span className="access-denied-step-number">{`${index + 1}.`}</span>
                      <span>{renderCopy(step)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="access-denied-actions">
              {!_.isEmpty(copy.buttonUrl) && !_.isEmpty(copy.buttonLabel) && (
                <a
                  href={copy.buttonUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="workspace-action-btn workspace-action-btn-primary"
                >
                  <Icon name="external alternate" style={iconStyle} />
                  {copy.buttonLabel}
                </a>
              )}
              <button
                className="workspace-action-btn workspace-action-btn-secondary"
                disabled={processing}
                onClick={this.handleLogout}
              >
                <Icon name="sign-out" style={iconStyle} />
                Log Out
              </button>
            </div>

            {!_.isEmpty(copy.helpEmail) && (
              <p className="access-denied-help">
                {copy.helpText} <a href={`mailto:${copy.helpEmail}`}>{copy.helpEmail}</a>
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }
}

decorate(UserApplication, {
  logoutProcessing: observable,
  currentUser: observable,
});

export default inject(
  'userStore',
  'usersStore',
  'userRolesStore',
  'awsAccountsStore',
  'indexesStore',
  'authentication',
)(withRouter(observer(UserApplication)));
